import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import * as Apollo from '@apollo/client'
import { useApolloClient, useQuery } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil, Share } from '@factorialco/f0-react/icons/app'
import {
  CompensationLegalEntitiesDocument,
  CompensationLocationsDocument,
  CompensationTeamsDocument,
  CompensationsListColumnsDocument,
  CompensationsListColumnsQuery,
  CompensationsListColumnsQueryVariables,
  CompensationsListFragment,
  CompensationsValueObjectsEmployeeCompensationValidationTypeEnum,
  EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment,
  EmployeePayrollRunInputRowPayrollRunCompensationFragment,
  EmployeePayrollRunTotalsFragment,
  EmployeePayrollRunsDocument,
  IntegrationsAgreggatedSyncableStateValuesEnum,
  ValidationTypeFiltersDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import {
  EmployeePayrollRunNode,
  employeePayrollRunElement,
  formatCellValue,
} from 'modules/compensations/components/EmployeePayrollRunElements'
import { useCompensationsRefresh } from 'modules/compensations/hooks/useCompensationsRefresh'
import { useCurrencyContext } from 'modules/compensations/lib/CurrencyContext'
import { usePayrollRuns } from 'modules/compensations/lib/PayrollRunsContext'
import { CompensationsFilters } from 'modules/compensations/types/filters'
import { useSync } from 'modules/integrations/hooks/useSync'
import { getBadgeGroupedBySyncables } from 'modules/integrations/lib/syncableStatus'
// TODO: We should avoid coupling modules because it hurts application performance - in order to execute this
//  compensations page, browser will have to upload the payroll_bundle as well, so, a good approach would be to
//  move this logic to the core or potentially to the compensations module. When deprecating the old payroll product we'll
//  have to migrate payroll concepts inside Compensation and this logic as well, but until then the same concept will
//  work for different products
import {
  getUnits,
  isUnitKey,
} from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

import { getHeaderOptions } from './utils'

/**
 * __useCompensationsListColumnsQuery__
 *
 * To run a query within a React component, call `useCompensationsListColumnsQuery` and pass it any options that fit your needs.
 * When your component renders, `useCompensationsListColumnsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCompensationsListColumnsQuery({
 *   variables: {
 *      ids: // array of 'payrollRunIds'
 *   },
 * });
 */
function useCompensationsListColumnsQuery(
  baseOptions: Apollo.QueryHookOptions<
    CompensationsListColumnsQuery,
    CompensationsListColumnsQueryVariables
  >
) {
  const options = { ...baseOptions }
  return Apollo.useQuery<CompensationsListColumnsQuery, CompensationsListColumnsQueryVariables>(
    CompensationsListColumnsDocument,
    options
  )
}

type CompensationsListProps = {
  payrollRun: CompensationsListFragment
  onEdit: (employeePayrollRunInputRowEmployeePayrollRun: EmployeePayrollRunNode) => void
  locale: string
  refreshFnRef: React.MutableRefObject<(() => void) | null>
}

type CompensationsListFilters = {
  companyId: number
  featureFlagNames: string[]
  payrollRunId: number
  legalEntityIds?: number[]
  teamIds?: number[]
  workplaceIds?: number[]
  validationTypes?: CompensationsValueObjectsEmployeeCompensationValidationTypeEnum[]
  integrationsAgreggatedSyncableStates?: IntegrationsAgreggatedSyncableStateValuesEnum[]
}

function CompensationsList({ payrollRun, onEdit, locale, refreshFnRef }: CompensationsListProps) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [currency] = useCurrencyContext()

  const [filters, setCompensationsFilters] = useState<CompensationsListFilters | undefined>()

  const { refreshKey } = useCompensationsRefresh({ refreshFnRef })

  const { data } = useCompensationsListColumnsQuery({
    variables: filters,
    skip: !filters,
  })

  const { marketplaceIntegration, syncEnabled, syncLoading, setFilters } = useSync()

  const { currentPayrollRun, payrollRuns, setCurrentPayrollRun } = usePayrollRuns()

  const { data: validationFiltersData } = useQuery(ValidationTypeFiltersDocument, {
    variables: {
      payrollRunId: payrollRun.id,
    },
  })

  const validationFiltersDataResult = validationFiltersData?.compensations

  const headerElements = useMemo(
    () => data?.compensations.elementPayrollConceptsConnection.nodes ?? [],
    [data?.compensations.elementPayrollConceptsConnection.nodes]
  )

  const hasPayrollResultConcepts = headerElements.some(
    (element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) =>
      element.resultType === 'payroll_result'
  )

  const countAlerts = (item: EmployeePayrollRunNode) => {
    const compensationsWithValidations = item.compensations.filter(
      (compensation) => compensation.validations && compensation.validations.length > 0
    )

    return compensationsWithValidations.length
  }

  const conceptColumns = useMemo(
    () =>
      headerElements.map((element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) => {
        const options = hasPayrollResultConcepts ? getHeaderOptions(element.resultType) : null

        return {
          label: element.label,
          summary: element.id,
          infoIcon: options?.infoIcon,
          info: options?.info,
          render: (item: EmployeePayrollRunNode) => {
            const employeePayrollRunCompensation = item?.compensations?.find(
              (comp: EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment) =>
                comp.payrollConcept?.id === element.payrollConceptId &&
                comp.resultType === element.resultType
            )
            const unitsConfiguration = getUnits({ money: currency.isoCode })

            return employeePayrollRunElement({
              employeePayrollElement: employeePayrollRunCompensation,
              locale,
              unitsConfiguration,
            })
          },
        }
      }),
    [locale, headerElements, hasPayrollResultConcepts]
  )

  const createPayrollTotalsMap = (
    payrollRunTotals: EmployeePayrollRunTotalsFragment[],
    headerColumns: EmployeePayrollRunInputRowPayrollRunCompensationFragment[],
    currencyIsoCode?: string
  ): Record<string, string> => {
    const result: Record<string, string> = {}

    headerColumns.forEach((column) => {
      result[column.id] = '-'
    })

    payrollRunTotals.forEach((total) => {
      const totalElement = headerColumns.find((e) => e.id === total.id)

      if (totalElement?.name && total.totalAmountInCents != null) {
        const unit = totalElement.payrollConceptUnit

        if (unit && isUnitKey(unit)) {
          const unitSymbol = getUnits({ money: currencyIsoCode })[unit]

          result[totalElement.id] = formatCellValue(
            unit,
            unitSymbol,
            total.totalAmountInCents,
            locale
          )
        }
      }
    })

    return result
  }

  const summaries = useMemo(() => {
    const result: { [key: string]: { type: 'sum' } } = {}

    headerElements.forEach((element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) => {
      result[element.id] = {
        type: 'sum',
      }
    })

    return result
  }, [headerElements])

  const [defaultDate] = useState<Date>(() =>
    currentPayrollRun ? new Date(currentPayrollRun.startsOn) : new Date()
  )

  const dateRange = useMemo(() => {
    const startsOnDates = payrollRuns.map((run) => new Date(run.startsOn))
    const endsOnDates = payrollRuns.map((run) => new Date(run.endsOn))

    return {
      minDate: new Date(Math.min(...startsOnDates.map((d) => d.getTime()))),
      maxDate: new Date(Math.max(...endsOnDates.map((d) => d.getTime()))),
    }
  }, [payrollRuns])

  const getBadge = (item: EmployeePayrollRunNode) => {
    if (!syncEnabled) return undefined

    const syncableCompensations = item.compensations.filter(
      (compensation) => compensation.resultType === 'compensation'
    )

    if (!syncableCompensations?.length) return undefined

    return getBadgeGroupedBySyncables(
      syncLoading,
      syncableCompensations.map((compensation) => compensation.syncableState?.[0])
    )
  }

  const countItemsWithAlerts = useCallback(
    (validationType: string) => {
      return () => {
        if (!validationFiltersDataResult) return 0

        if (validationType === 'above_limit') {
          return validationFiltersDataResult.aboveLimit?.totalCount ?? 0
        }

        if (validationType === 'pending_to_define') {
          return validationFiltersDataResult.pendingToDefine?.totalCount ?? 0
        }

        if (validationType === 'discrepancy') {
          return validationFiltersDataResult.discrepancy?.totalCount ?? 0
        }

        return 0
      }
    },
    [validationFiltersDataResult]
  )

  const source = useGraphqlDataCollectionSource(
    {
      document: EmployeePayrollRunsDocument,
      fetchPolicy: 'network-only',
      mapData: (data) => data.compensations.payrollRunEmployeesConnection.nodes,
      totalItemSummary: (totalItems) =>
        totalItems === 1
          ? i18n.t('compensations.common.employees.one', { totalItems })
          : i18n.t('compensations.common.employees.other', {
              totalItems,
            }),
      paginationType: 'infinite-scroll',
      summaries,
      mapSummaries: (data) => {
        return data.compensations.payrollRunEmployeesConnection.nodes[0]
          ? {
              ...data.compensations.payrollRunEmployeesConnection.nodes[0],
              ...createPayrollTotalsMap(
                data.compensations.payrollRunTotalsConnection.nodes ?? [],
                headerElements,
                currency.isoCode
              ),
            }
          : undefined
      },
      mapPagination: (data) => {
        return {
          total: data.compensations.payrollRunEmployeesConnection.totalCount ?? 0,
          cursor: data.compensations.payrollRunEmployeesConnection.pageInfo.endCursor ?? '0',
          hasNextPage:
            data.compensations.payrollRunEmployeesConnection.pageInfo.hasNextPage ?? false,
        }
      },
      mapVariables: ({ navigationFilters, sortings, search, filters }) => {
        const legalEntityIds = filters?.legalEntityIds?.map(Number).filter(Boolean) || undefined
        const teamIds = filters?.teamIds?.map(Number).filter(Boolean) || undefined
        const workplaceIds = filters?.workplaceIds?.map(Number).filter(Boolean) || undefined
        const integrationsAgreggatedSyncableStatesFromFilters =
          filters?.integrationsAgreggatedSyncableStates?.filter(Boolean) || undefined

        const isValidSyncableStateType = (
          type: string
        ): type is IntegrationsAgreggatedSyncableStateValuesEnum => {
          return ['synced', 'partially_synced', 'outdated', 'failed', 'pending'].includes(type)
        }

        const isValidValidationType = (
          type: string
        ): type is CompensationsValueObjectsEmployeeCompensationValidationTypeEnum => {
          return type === 'above_limit' || type === 'pending_to_define' || type === 'discrepancy'
        }

        const validationTypes =
          filters?.validationTypes?.filter(Boolean).filter(isValidValidationType) || undefined

        // TODO: this is a hack to set the filters in the parent component until
        // the DataOneCollection is updated to support columns dynamically
        const currentFilters = {
          companyId,
          featureFlagNames: [],
          payrollRunId: payrollRun.id,
          legalEntityIds,
          teamIds,
          workplaceIds,
          validationTypes,
          integrationsAgreggatedSyncableStates:
            integrationsAgreggatedSyncableStatesFromFilters?.filter(isValidSyncableStateType) ||
            undefined,
        }

        const syncFilters: CompensationsFilters = {
          legalEntityIds: currentFilters.legalEntityIds,
          teamIds: currentFilters.teamIds,
          workplaceIds: currentFilters.workplaceIds,
          validationTypes:
            filters?.validationTypes?.filter(Boolean).filter(isValidValidationType) || undefined,
          integrationsAgreggatedSyncableStates:
            currentFilters.integrationsAgreggatedSyncableStates?.map((state) => state.toString()),
        }

        setCompensationsFilters(currentFilters)

        setFilters(syncFilters)

        const integrationsAgreggatedSyncableStates =
          integrationsAgreggatedSyncableStatesFromFilters?.filter(isValidSyncableStateType) ||
          undefined

        if (!navigationFilters?.date?.value?.from || !navigationFilters?.date?.value?.to) {
          return {
            payrollRunId: payrollRun.id,
            search: search,
            sort: sortings?.[0],
            legalEntityIds,
            teamIds,
            workplaceIds,
            marketplaceIntegrationId: marketplaceIntegration?.id || 0,
            includeSyncableState: !!marketplaceIntegration,
            validationTypes,
            integrationsAgreggatedSyncableStates,
            companyId,
          }
        }

        const now = moment()
        const from = moment(navigationFilters?.date.value.from) || now.startOf('month')
        const to = moment(navigationFilters?.date.value.to) || now.endOf('month')
        const payrollRunId = payrollRuns.find((element) => {
          return (
            moment(element.startsOn).isSameOrAfter(from) &&
            moment(element.endsOn).isSameOrBefore(to)
          )
        })?.id

        if (payrollRunId && payrollRunId !== currentPayrollRun?.id) {
          setCurrentPayrollRun(Number(payrollRunId))
        }

        return {
          payrollRunId: payrollRunId || payrollRun.id,
          search: search,
          sortOrder: sortings?.[0],
          legalEntityIds,
          teamIds,
          workplaceIds,
          marketplaceIntegrationId: marketplaceIntegration?.id || 0,
          includeSyncableState: !!marketplaceIntegration,
          validationTypes,
          integrationsAgreggatedSyncableStates,
          companyId,
        }
      },
      navigationFilters: {
        date: {
          type: 'date-navigator',
          defaultValue: defaultDate,
          granularity: 'month',
          max: dateRange.maxDate,
          min: dateRange.minDate,
          hideGoToCurrent: true,
        },
      },
      search: {
        enabled: true,
      },
      defaultSorting: {
        field: 'fullName',
        order: 'asc',
      },
      filters: {
        legalEntityIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.legalEntityColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: CompensationLegalEntitiesDocument,
                variables: {
                  companyId,
                },
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.apiCore?.company?.legalEntities?.map((legalEntity) => ({
                  label: legalEntity.legalName || '',
                  value: Number(legalEntity.id),
                })) || []
              )
            },
          },
        },
        teamIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.teamColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: CompensationTeamsDocument,
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.teams.teamsConnection.nodes.map((team) => ({
                  label: team.name || '',
                  value: Number(team.id),
                })) || []
              )
            },
          },
        },
        workplaceIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.workplaceColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: async () => {
              const result = await client.query({
                query: CompensationLocationsDocument,
                fetchPolicy: 'cache-first',
              })

              return (
                result.data?.locations.locationsConnection.nodes.map((location) => ({
                  label: location.name || '',
                  value: Number(location.id),
                })) || []
              )
            },
          },
        },
        integrationsAgreggatedSyncableStates: {
          label: i18n.t('integrations.filters.sync_status.sync_status_label'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('integrations.filters.sync_status.synced'),
                value: 'synced',
              },
              {
                label: i18n.t('integrations.filters.sync_status.partially_synced'),
                value: 'partially_synced',
              },
              {
                label: i18n.t('integrations.filters.sync_status.outdated'),
                value: 'outdated',
              },
              {
                label: i18n.t('integrations.filters.sync_status.failed'),
                value: 'failed',
              },
              {
                label: i18n.t('integrations.filters.sync_status.pending'),
                value: 'pending',
              },
            ].filter((option) => option && option.value != null),
          },
        },
        ...{
          validationTypes: {
            label: i18n.t(
              'compensations.PayrollRunCompensationsList.filters.validationTypeColumnLabel'
            ),
            type: 'in',
            options: {
              options: [
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.pendingAlertColumnLabel'
                  ),
                  value: 'pending_to_define',
                },
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.warningAlertColumnLabel'
                  ),
                  value: 'above_limit',
                },
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.discrepancyAlertColumnLabel'
                  ),
                  value: 'discrepancy',
                },
              ].filter((option) => option && option.value != null),
            },
          },
        },
      },
      presets: [
        ...(countItemsWithAlerts('above_limit')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.warningAlertColumnLabel'
                ),
                filter: { validationTypes: ['above_limit'] },
                itemsCount: countItemsWithAlerts('above_limit'),
              },
            ]
          : []),
        ...(countItemsWithAlerts('pending_to_define')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.pendingAlertColumnLabel'
                ),
                filter: { validationTypes: ['pending_to_define'] },
                itemsCount: countItemsWithAlerts('pending_to_define'),
              },
            ]
          : []),
        ...(countItemsWithAlerts('discrepancy')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.discrepancyAlertColumnLabel'
                ),
                filter: { validationTypes: ['discrepancy'] },
                itemsCount: countItemsWithAlerts('discrepancy'),
              },
            ]
          : []),
      ],
      itemOnClick: (item: EmployeePayrollRunNode) => () => onEdit(item),
      itemActions: (item: EmployeePayrollRunNode) => [
        {
          label: i18n.t('compensations.PayrollRunCompensationsList.updateActionLabel'),
          icon: Pencil,
          onClick: () => {
            item.id && onEdit(item)
          },
        },
        {
          label: i18n.t('compensations.PayrollRunCompensationsList.goToContractActionLabel'),
          icon: Share,
          onClick: () => {
            item.employee?.id &&
              window.open(
                navigation.employees.id.contractVersions.fullPath({
                  id: String(item.employee.id),
                }),
                '_blank'
              )
          },
        },
      ],
    },
    [
      conceptColumns,
      defaultDate,
      dateRange,
      payrollRuns,
      currentPayrollRun,
      setCurrentPayrollRun,
      navigation,
      navigate,
      onEdit,
      locale,
      headerElements,
      refreshKey,
    ]
  )

  const totalAlerts = useMemo(() => {
    return (
      countItemsWithAlerts('above_limit')() +
      countItemsWithAlerts('pending_to_define')() +
      countItemsWithAlerts('discrepancy')()
    )
  }, [countItemsWithAlerts])

  type RendererDefinition =
    | { type: 'text'; value: string }
    | { type: 'status'; value: { status: string; label: string } }

  return (
    <OneDataCollection
      fullHeight
      emptyStates={{
        'no-data': {
          title: i18n.t('compensations.employeePayrollRunElementCell.empty'),
        },
        'no-results': {
          title: i18n.t('compensations.employeePayrollRunElementCell.empty_results.title'),
          description: i18n.t(
            'compensations.employeePayrollRunElementCell.empty_results.description'
          ),
          emoji: '🧐',
        },
      }}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('compensations.PayrollRunCompensationsList.employeeColumnLabel'),
                sorting: 'fullName',
                render: (item: EmployeePayrollRunNode) => {
                  const firstName = item.employee?.firstName ?? ''
                  const lastName = item.employee?.lastName ?? ''
                  const name = [firstName, lastName].filter(Boolean).join(' ')
                  return {
                    type: 'person',
                    value: {
                      firstName,
                      lastName,
                      name,
                      src: buildUrl(item.employee?.avatar?.url ?? '') ?? '',
                      badge: getBadge(item),
                    },
                  }
                },
              },
              ...(totalAlerts > 0
                ? [
                    {
                      label: i18n.t('compensations.PayrollRunCompensationsList.alertsColumnLabel'),
                      info: i18n.t('compensations.PayrollRunCompensationsList.alertsColumnTooltip'),
                      render: (item: EmployeePayrollRunNode): RendererDefinition => {
                        const alertCount = countAlerts(item)

                        if (alertCount > 0) {
                          return {
                            type: 'status',
                            value: {
                              status: 'warning',
                              label: String(alertCount),
                            },
                          }
                        }

                        return {
                          type: 'text',
                          value: '-',
                        }
                      },
                    },
                  ]
                : []),
              ...conceptColumns,
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}

export { CompensationsList }
